import { Context, Data, Effect, Layer } from "effect";

import { InvoiceConfig, RepositoryError } from "@invoiceddd/domain";
import {
  InvoiceNumberRepository,
  InvoiceNumberRepositorySchema,
} from "../ports/InvoiceNumberRepository";

/**
 * Domain error for invoice number generation failures
 */
export class InvoiceNumberGenerationError extends Data.TaggedError(
  "@/InvoiceNumberGenerationError"
)<{
  readonly cause?: unknown;
  readonly message: string;
}> {}

/**
 * Domain service for generating unique invoice numbers.
 * This service generates incremental invoice numbers following the pattern INV-XXXX.
 *
 * This requires I/O to check existing numbers and thus must not be used within the Domain.
 * Use it in the application layer.
 */
export interface InvoiceNumberGeneratorSchema {
  /**
   * Generates a new unique invoice number following incremental pattern
   *
   * @returns Effect that resolves to a unique invoice number or fails with generation error
   */
  readonly generate: () => Effect.Effect<string, InvoiceNumberGenerationError | RepositoryError>;
}

/**
 * Context tag for InvoiceNumberGenerator service
 */
export const InvoiceNumberGenerator = Context.GenericTag<InvoiceNumberGeneratorSchema>(
  "@/InvoiceNumberGenerator"
);

/**
 * Context tag for InvoiceConfig
 */
export const InvoiceConfigTag = Context.GenericTag<InvoiceConfig>("@/InvoiceConfig");

/**
 * Implementation of InvoiceNumberGenerator service
 */
export const InvoiceNumberGeneratorLive = Layer.effect(
  InvoiceNumberGenerator,
  Effect.gen(function* () {
    const repository = yield* InvoiceNumberRepository;
    const invoiceConfig = yield* InvoiceConfigTag;

    const implementation: InvoiceNumberGeneratorSchema = {
      generate: () => {
        return Effect.gen(function* () {
          // Find the highest existing invoice number to generate the next incremental one
          const nextNumber = yield* findNextIncrementalNumber(repository, invoiceConfig);

          // Format based on configuration
          const invoiceNumber = formatInvoiceNumber(nextNumber, invoiceConfig);

          // Double-check uniqueness (should always be unique, but safety first)
          const exists = yield* repository.existsByInvoiceNumber(invoiceNumber);

          if (exists) {
            return yield* Effect.fail(
              new InvoiceNumberGenerationError({
                message: `Generated invoice number '${invoiceNumber}' already exists. This should not happen.`,
              })
            );
          }

          yield* Effect.annotateCurrentSpan({
            generatedInvoiceNumber: invoiceNumber,
            nextNumber,
            prefix: invoiceConfig.prefix,
          });

          return invoiceNumber;
        }).pipe(Effect.withSpan("InvoiceNumberGenerator.generate"));
      },
    };

    return InvoiceNumberGenerator.of(implementation);
  })
);

/**
 * Formats an invoice number based on configuration
 */
const formatInvoiceNumber = (number: number, config: InvoiceConfig): string => {
  const paddedNumber = number.toString().padStart(4, "0");

  if (config.prefix) {
    return `${config.prefix}-${paddedNumber}`;
  }

  return paddedNumber;
};

/**
 * Helper function to find the next incremental number
 * This is a simplified implementation that uses a basic approach.
 * In a production system, this might use database sequences or atomic counters.
 */
const findNextIncrementalNumber = (
  repository: InvoiceNumberRepositorySchema,
  config: InvoiceConfig
): Effect.Effect<number, InvoiceNumberGenerationError | RepositoryError> => {
  return Effect.gen(function* () {
    // Start with a reasonable base number for new systems
    let candidate = 1001;
    const maxAttempts = 1000; // Prevent infinite loops
    let attempts = 0;

    // Find the first available number by checking incrementally
    // This is not the most efficient approach, but it's simple and works
    // In production, you'd want to query for the MAX invoice number and increment
    while (attempts < maxAttempts) {
      const candidateNumber = formatInvoiceNumber(candidate, config);
      const exists = yield* repository.existsByInvoiceNumber(candidateNumber);

      if (!exists) {
        yield* Effect.annotateCurrentSpan({
          foundAvailableNumber: candidate,
          attempts: attempts + 1,
        });
        return candidate;
      }

      candidate++;
      attempts++;
    }

    // If we've tried too many numbers, something is wrong
    return yield* Effect.fail(
      new InvoiceNumberGenerationError({
        message: `Could not find available invoice number after ${maxAttempts} attempts. Starting from ${candidate - maxAttempts}.`,
      })
    );
  }).pipe(Effect.withSpan("InvoiceNumberGenerator.findNextIncrementalNumber"));
};
